import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Check,
  Camera,
  Sparkles,
  ScanFace,
  Circle,
  SlidersHorizontal,
  ShoppingBag,
  ExternalLink,
  Heart,
  Star,
} from "lucide-react";

/**
 * DiscoverShowcase — landing-page walkthrough of the app.
 *
 * Hand-built phone mockups (not screenshots) so the section stays in
 * sync with the product's design language and scales crisply on any
 * viewport. Seven screens mirror the real user journey:
 *
 *   01 Onboarding question
 *   02 Live selfie capture
 *   03 "Analyzing" moment
 *   04 A routine step card
 *   05 Product recommendations + affiliate shop links
 *   06 AR mirror with face overlay
 *   07 Glow journal (capture + saved routines)
 */

// ── Reusable face illustration ────────────────────────────────────────
// A stylized portrait used across multiple screens so the
// AR overlays and step schematics read as "the same person."
const FaceArt = ({
  showLashes = false,
  showLipColor = false,
  showBlush = false,
  showBrow = true,
  className = "",
}) => (
  <svg
    viewBox="0 0 160 220"
    className={className}
    preserveAspectRatio="xMidYMid slice"
  >
    {/* neck + shoulders */}
    <path
      d="M50 215 C 55 180 55 168 60 152 L 100 152 C 105 168 105 180 110 215 Z"
      fill="#7A4E55"
      opacity="0.9"
    />
    {/* face */}
    <ellipse cx="80" cy="100" rx="38" ry="52" fill="#D4A69A" />
    {/* Hair — soft, longer */}
    <path
      d="M44 86 C 38 50 60 30 80 30 C 100 30 122 50 116 86 C 114 100 112 108 108 112 C 105 88 99 72 92 66 C 88 80 86 84 80 84 C 74 84 72 80 68 66 C 61 72 55 88 52 112 C 48 108 46 100 44 86 Z"
      fill="#2F1F24"
    />
    {/* tiny earring sparkle */}
    <circle cx="118" cy="112" r="2.2" fill="#F4D694" />
    {/* eyes */}
    <ellipse cx="67" cy="100" rx="4.2" ry="2.5" fill="#2C1820" />
    <ellipse cx="93" cy="100" rx="4.2" ry="2.5" fill="#2C1820" />
    <circle cx="66" cy="99" r="0.9" fill="#FFF" opacity="0.9" />
    <circle cx="92" cy="99" r="0.9" fill="#FFF" opacity="0.9" />
    {/* lashes */}
    {showLashes && (
      <g stroke="#1A0F14" strokeWidth="0.8" strokeLinecap="round">
        <line x1="62" y1="97" x2="60.5" y2="95.5" />
        <line x1="65" y1="96.5" x2="64" y2="94.5" />
        <line x1="69" y1="96.5" x2="68.5" y2="94.5" />
        <line x1="72" y1="97" x2="71.5" y2="95.5" />
        <line x1="88" y1="97" x2="87.5" y2="95.5" />
        <line x1="91" y1="96.5" x2="90.5" y2="94.5" />
        <line x1="95" y1="96.5" x2="95.5" y2="94.5" />
        <line x1="98" y1="97" x2="99.5" y2="95.5" />
      </g>
    )}
    {/* brows */}
    {showBrow && (
      <>
        <path
          d="M60 90 Q 67 85 74 90"
          stroke="#2C1820"
          strokeWidth="2.2"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M86 90 Q 93 85 100 90"
          stroke="#2C1820"
          strokeWidth="2.2"
          fill="none"
          strokeLinecap="round"
        />
      </>
    )}
    {/* nose */}
    <path
      d="M80 104 Q 78 116 80 122 Q 83 122 82 118"
      stroke="#8E5D63"
      strokeWidth="1.2"
      fill="none"
      strokeLinecap="round"
    />
    {/* blush */}
    {showBlush && (
      <>
        <ellipse cx="63" cy="118" rx="7" ry="5" fill="#D27A83" opacity="0.55" />
        <ellipse cx="97" cy="118" rx="7" ry="5" fill="#D27A83" opacity="0.55" />
      </>
    )}
    {/* lips */}
    <path
      d="M70 135 Q 80 131 90 135 Q 80 141 70 135 Z"
      fill={showLipColor ? "#B84359" : "#A04554"}
    />
    <path
      d="M70 135 Q 80 137 90 135"
      stroke="#6B2733"
      strokeWidth="0.6"
      fill="none"
    />
    {showLipColor && (
      <path
        d="M74 134 Q 80 132 86 134"
        stroke="#F4C4CE"
        strokeWidth="0.5"
        fill="none"
        opacity="0.6"
      />
    )}
  </svg>
);

// ── Phone frame shell ─────────────────────────────────────────────────
const PhoneFrame = ({ children, testid }) => (
  <div
    className="relative w-[250px] h-[520px] rounded-[40px] bg-[#1B1315] p-2 shadow-[0_20px_50px_rgba(44,33,35,0.25)]"
    data-testid={testid}
  >
    <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-20 rounded-full bg-[#1B1315] z-20" />
    <div className="relative w-full h-full rounded-[32px] overflow-hidden bg-[#FBF4EF]">
      {children}
    </div>
  </div>
);

// ── Screen 1 — Onboarding question ────────────────────────────────────
const ScreenOnboarding = () => {
  const { t } = useTranslation();
  return (
  <div className="absolute inset-0 flex flex-col bg-gradient-to-b from-[#FDFBF7] to-[#F8EEEE] px-5 pt-7 pb-5">
    <div className="text-[9px] uppercase tracking-[0.25em] text-[#9B6A6C] mb-2">
      Step 1 of 6
    </div>
    <div className="font-serif italic text-[17px] text-[#2C2123] leading-tight mb-1">
      {t("discover.onboardingQuestion")}
    </div>
    <div className="text-[10px] text-[#5C4B51] mb-3 leading-snug">
      {t("discover.onboardingHint")}
    </div>
    <div className="space-y-2">
      {[
        { l: "Soft radiance", active: false },
        { l: "Everyday beauty", active: true },
        { l: "Full glam", active: false },
      ].map((o) => (
        <div
          key={o.l}
          className={
            o.active
              ? "rounded-xl border border-[#9B6A6C] bg-white px-3 py-2.5 flex items-center justify-between shadow-sm"
              : "rounded-xl border border-[#EADDD7] bg-white/70 px-3 py-2.5 flex items-center justify-between"
          }
        >
          <span
            className={
              o.active
                ? "text-[11px] font-medium text-[#2C2123]"
                : "text-[11px] text-[#5C4B51]"
            }
          >
            {o.l}
          </span>
          {o.active ? (
            <span className="h-4 w-4 rounded-full bg-[#9B6A6C] flex items-center justify-center">
              <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
            </span>
          ) : (
            <Circle className="h-3.5 w-3.5 text-[#D5A8A4]" />
          )}
        </div>
      ))}
    </div>
    <div className="mt-auto">
      <div className="h-8 rounded-full bg-[#9B6A6C] text-white text-[10px] flex items-center justify-center gap-1">
        Continue <ArrowRight className="h-3 w-3" />
      </div>
    </div>
  </div>
  );
};

// ── Screen 2 — Selfie capture ─────────────────────────────────────────
const ScreenSelfie = () => {
  const { t } = useTranslation();
  return (
  <div className="absolute inset-0 bg-[#1D1517] flex flex-col">
    <div className="relative flex-1 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_40%,#B8918A_0%,#6B4852_45%,#1D1517_90%)]" />
      <FaceArt
        showBrow
        showLashes
        showLipColor={false}
        className="absolute inset-0 w-full h-full"
      />
      {/* scan-corner brackets at each corner */}
      {["top-3 left-3", "top-3 right-3", "bottom-3 left-3", "bottom-3 right-3"].map(
        (pos) => {
          const isTop = pos.startsWith("top");
          const isLeft = pos.includes("left");
          return (
            <div key={pos} className={`absolute ${pos} h-5 w-5`}>
              <div
                className={`absolute ${isTop ? "top-0" : "bottom-0"} ${
                  isLeft ? "left-0" : "right-0"
                } h-[2px] w-5 bg-white/80 rounded-full`}
              />
              <div
                className={`absolute ${isTop ? "top-0" : "bottom-0"} ${
                  isLeft ? "left-0" : "right-0"
                } w-[2px] h-5 bg-white/80 rounded-full`}
              />
            </div>
          );
        }
      )}
      <div className="absolute top-3 left-0 right-0 flex justify-center">
        <div className="px-2.5 py-1 rounded-full bg-black/40 text-white text-[9px] backdrop-blur-sm">
          {t("discover.selfiePill")}
        </div>
      </div>
    </div>
    {/* shutter */}
    <div className="h-20 bg-black/60 backdrop-blur-sm flex items-center justify-center gap-5">
      <div className="h-8 w-8 rounded-lg bg-white/10" />
      <div className="h-12 w-12 rounded-full bg-white/15 border-[3px] border-white/85 flex items-center justify-center shadow-[0_6px_20px_rgba(0,0,0,0.3)]">
        <div className="h-8 w-8 rounded-full bg-white" />
      </div>
      <div className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center">
        <Camera className="h-3.5 w-3.5 text-white/70" />
      </div>
    </div>
  </div>
  );
};

// ── Screen 3 — Analyzing animation ───────────────────────────────────
const ScreenAnalyzing = () => {
  const { t } = useTranslation();
  return (
  <div className="absolute inset-0 bg-gradient-to-b from-[#FCEEE6] via-[#F5E0D8] to-[#E5C9BF] flex flex-col items-center justify-center px-6">
    <div className="relative h-36 w-36 mb-6">
      {/* expanding rings */}
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="absolute inset-0 rounded-full border-2 border-[#9B6A6C]"
          style={{
            opacity: 0.3 + i * 0.1,
            transform: `scale(${1 + i * 0.12})`,
          }}
        />
      ))}
      <div className="absolute inset-4 rounded-full overflow-hidden border-2 border-white">
        <FaceArt showBrow showLashes className="w-full h-full" />
        {/* shimmer sweep */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-transparent" />
      </div>
      <Sparkles className="absolute -top-2 -right-2 h-5 w-5 text-[#9B6A6C]" />
    </div>
    <div className="text-[11px] uppercase tracking-[0.25em] text-[#9B6A6C] mb-2">
      {t("discover.analyzingKicker")}
    </div>
    <div className="font-serif italic text-[17px] text-[#2C2123] text-center leading-tight mb-3">
      {t("discover.analyzingHeadline")}
    </div>
    <div className="text-[10px] text-[#5C4B51] text-center leading-snug max-w-[180px]">
      {t("discover.analyzingBody")}
    </div>
    <div className="mt-6 flex gap-1.5">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-[#9B6A6C]"
          style={{ opacity: 0.4 + i * 0.2 }}
        />
      ))}
    </div>
  </div>
  );
};

// ── Screen 4 — Routine step card ──────────────────────────────────────
const ScreenRoutine = () => {
  const { t } = useTranslation();
  return (
  <div className="absolute inset-0 flex flex-col bg-gradient-to-b from-[#FDFBF7] to-[#F5EAE5] px-4 pt-7 pb-5">
    <div className="flex items-center justify-between mb-3">
      <div className="text-[9px] uppercase tracking-[0.25em] text-[#9B6A6C]">
        Step 3 of 7
      </div>
      <div className="flex gap-0.5">
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className={
              i <= 2
                ? "h-1 w-2 rounded-full bg-[#9B6A6C]"
                : "h-1 w-2 rounded-full bg-[#EADDD7]"
            }
          />
        ))}
      </div>
    </div>
    <div className="rounded-2xl bg-white border border-[#EADDD7] p-3 shadow-sm">
      <div className="text-[9px] uppercase tracking-[0.22em] text-[#D4B483] mb-1">
        Cheek glow
      </div>
      <div className="font-serif italic text-[15px] text-[#2C2123] leading-tight mb-2">
        {t("discover.routineTitle")}
      </div>
      {/* mini face schematic with cheek zones highlighted */}
      <div className="relative h-[90px] bg-gradient-to-b from-[#F8EEEE] to-[#F2E0DA] rounded-xl mb-2 overflow-hidden">
        <FaceArt showBlush showBrow className="w-full h-full" />
        <div className="absolute bottom-1 right-1.5 text-[7px] uppercase tracking-[0.2em] text-[#9B6A6C] bg-white/90 rounded px-1.5 py-0.5">
          Swirl
        </div>
      </div>
      <p className="text-[9px] text-[#5C4B51] leading-snug">
        {t("discover.routineBody")}
      </p>
    </div>
    <div className="mt-auto flex gap-2">
      <div className="h-7 w-7 rounded-full border border-[#D5A8A4] flex items-center justify-center text-[#9B6A6C]">
        <ArrowRight className="h-3 w-3 rotate-180" />
      </div>
      <div className="flex-1 h-7 rounded-full bg-[#9B6A6C] text-white text-[9px] flex items-center justify-center gap-1">
        Got it <ArrowRight className="h-3 w-3" />
      </div>
    </div>
  </div>
  );
};

// ── Screen 5 — Product recommendations ────────────────────────────────
const ScreenProducts = () => {
  const { t } = useTranslation();
  return (
  <div className="absolute inset-0 flex flex-col bg-gradient-to-b from-[#FDFBF7] to-[#F5EAE5] px-4 pt-7 pb-5">
    <div className="text-[9px] uppercase tracking-[0.25em] text-[#9B6A6C] mb-1">
      {t("discover.productsKicker")}
    </div>
    <div className="font-serif italic text-[15px] text-[#2C2123] leading-tight mb-3">
      {t("discover.productsHeadline")}
    </div>
    <div className="space-y-2 flex-1 overflow-hidden">
      {[
        {
          brand: "ABOUT-FACE",
          name: "Light Lock Lip",
          price: "$18 · Amazon",
          band: "indie",
        },
        {
          brand: "KIMCHI CHIC",
          name: "Juicy Jelly Blush",
          price: "$16 · Ulta",
          band: "drag-owned",
        },
        {
          brand: "FENTY BEAUTY",
          name: "Cheeks Out Freestyle",
          price: "$24 · Sephora",
          band: "medium",
        },
      ].map((p) => (
        <div
          key={p.name}
          className="rounded-xl border border-[#EADDD7] bg-white p-2.5"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[8px] uppercase tracking-[0.18em] text-[#9B6A6C]">
                {p.brand}
              </div>
              <div className="text-[11px] font-medium text-[#2C2123] leading-tight truncate">
                {p.name}
              </div>
              <div className="text-[9px] text-[#8A7D82] mt-0.5">
                {p.price}
              </div>
            </div>
            <div className="shrink-0 inline-flex items-center gap-0.5 bg-[#9B6A6C] text-white text-[9px] rounded-full px-2 py-1">
              Shop
              <ExternalLink className="h-2.5 w-2.5" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-2 text-[8px] text-[#8A7D82] italic leading-snug">
      Affiliate links support TruBeauté · nothing in your routine
      depends on buying anything.
    </div>
  </div>
  );
};

// ── Screen 6 — AR mirror with human face + overlay ───────────────────
const ScreenMirror = () => {
  const { t } = useTranslation();
  return (
  <div className="absolute inset-0 bg-[#0F0B0C] flex flex-col">
    <div className="relative flex-1 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_35%,#8E5D63_0%,#3A2228_55%,#0F0B0C_92%)]" />
      {/* Human face illustration */}
      <FaceArt
        showBrow
        showLashes
        showLipColor
        showBlush
        className="absolute inset-0 w-full h-full"
      />
      {/* AR mesh + glowing eye-shimmer overlay */}
      <svg
        viewBox="0 0 160 220"
        className="absolute inset-0 w-full h-full pointer-events-none"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* thin face-mesh wireframe */}
        <g stroke="#D5A8A4" strokeWidth="0.25" opacity="0.4">
          {[46, 58, 70, 82, 94, 106, 118].map((x) => (
            <line key={`v${x}`} x1={x} y1="50" x2={x} y2="160" />
          ))}
          {[60, 80, 100, 120, 140].map((y) => (
            <line key={`h${y}`} x1="40" y1={y} x2="120" y2={y} />
          ))}
          {/* tracking dots */}
          {[
            [67, 100],
            [93, 100],
            [80, 104],
            [80, 135],
            [63, 118],
            [97, 118],
          ].map(([x, y]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r="1" fill="#FFE6D7" />
          ))}
        </g>
        {/* highlighted zones — what the current step guides */}
        <ellipse cx="67" cy="95" rx="10" ry="4" fill="#D4B483" opacity="0.55">
          <animate
            attributeName="opacity"
            values="0.3;0.75;0.3"
            dur="2.1s"
            repeatCount="indefinite"
          />
        </ellipse>
        <ellipse cx="93" cy="95" rx="10" ry="4" fill="#D4B483" opacity="0.55">
          <animate
            attributeName="opacity"
            values="0.3;0.75;0.3"
            dur="2.1s"
            repeatCount="indefinite"
          />
        </ellipse>
      </svg>
      {/* top pill */}
      <div className="absolute top-3 left-0 right-0 flex justify-center">
        <div className="px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-[9px] flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[#D4B483] animate-pulse" />
          {t("discover.mirrorTracking")}
        </div>
      </div>
      {/* bottom step hint */}
      <div className="absolute bottom-3 left-3 right-3">
        <div className="rounded-xl bg-white/12 backdrop-blur-md border border-white/20 px-3 py-2">
          <div className="text-[8px] uppercase tracking-[0.22em] text-[#D5A8A4] mb-0.5">
            {t("discover.mirrorStepLabel")}
          </div>
          <div className="text-[9px] text-white leading-snug">
            {t("discover.mirrorStepBody")}
          </div>
        </div>
      </div>
    </div>
    <div className="h-12 bg-black/60 backdrop-blur-sm flex items-center justify-around">
      <SlidersHorizontal className="h-3.5 w-3.5 text-white/70" />
      <div className="h-8 w-8 rounded-full border-2 border-white/80 flex items-center justify-center">
        <div className="h-4 w-4 rounded-full bg-white" />
      </div>
      <ScanFace className="h-3.5 w-3.5 text-white/70" />
    </div>
  </div>
  );
};

// ── Screen 7 — Glow journal ──────────────────────────────────────────
const ScreenJournal = () => {
  const { t } = useTranslation();
  return (
  <div className="absolute inset-0 flex flex-col bg-gradient-to-b from-[#FDFBF7] to-[#F5EAE5] px-4 pt-7 pb-5">
    <div className="text-[9px] uppercase tracking-[0.25em] text-[#9B6A6C] mb-1">
      {t("discover.journalKicker")}
    </div>
    <div className="font-serif italic text-[15px] text-[#2C2123] leading-tight mb-3">
      {t("discover.journalHeadline")}
    </div>
    {/* saved routines list */}
    <div className="space-y-2 mb-3">
      {[
        { title: "Soft everyday glow", date: "Yesterday", tone: "#D5A8A4" },
        { title: "Friday night glam", date: "2 days ago", tone: "#9B6A6C" },
        { title: "Brunch radiance", date: "Last week", tone: "#D4B483" },
      ].map((r, i) => (
        <div
          key={r.title}
          className="rounded-xl border border-[#EADDD7] bg-white p-2.5 flex items-center gap-2.5"
        >
          <div
            className="h-8 w-8 rounded-full flex items-center justify-center text-white shrink-0"
            style={{ backgroundColor: r.tone }}
          >
            <Heart className="h-3.5 w-3.5" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-medium text-[#2C2123] leading-tight truncate">
              {r.title}
            </div>
            <div className="text-[9px] text-[#8A7D82]">{r.date}</div>
          </div>
          <Star
            className={
              i === 0
                ? "h-3.5 w-3.5 text-[#D4B483] fill-[#D4B483]"
                : "h-3.5 w-3.5 text-[#EADDD7]"
            }
          />
        </div>
      ))}
    </div>
    {/* snapshot strip */}
    <div className="text-[8px] uppercase tracking-[0.2em] text-[#8A7D82] mb-1.5">
      Snapshots
    </div>
    <div className="flex gap-1.5">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-12 w-12 rounded-lg overflow-hidden border border-[#EADDD7] bg-[#2A1A20]"
        >
          <FaceArt
            showBrow
            showLipColor={i % 2 === 0}
            showBlush
            className="w-full h-full"
          />
        </div>
      ))}
    </div>
    <div className="mt-auto text-[8px] text-[#8A7D82] italic leading-snug">
      {t("discover.journalStealthNote")}
    </div>
  </div>
  );
};

// ── Step manifest ─────────────────────────────────────────────────────
const STEPS = [
  {
    n: "01",
    label: "Onboarding",
    title: "Share how you want to feel.",
    body:
      "Six gentle questions — goal, experience, finishes, what's in your drawer, and anything you'd rather skip.",
    Screen: ScreenOnboarding,
  },
  {
    n: "02",
    label: "Selfie",
    title: "Take a selfie.",
    body:
      "The AI reads your undertones and features. Your selfie is securely processed to create the routine and is not stored in TruBeauté's database.",
    Screen: ScreenSelfie,
  },
  {
    n: "03",
    label: "Analyzing",
    title: "Let Trouvé do the thinking.",
    body:
      "GPT-4o Vision filtered through our affirming guardrails layer — around twenty seconds, no errors, no small talk.",
    Screen: ScreenAnalyzing,
  },
  {
    n: "04",
    label: "Your routine",
    title: "A step-by-step that looks like you.",
    body:
      "Zone-by-zone guidance with face schematics, affirming language, and technique cues — sweep, swirl, tap, feather.",
    Screen: ScreenRoutine,
  },
  {
    n: "05",
    label: "Shop the picks",
    title: "Every product, one tap away.",
    body:
      "Trans-celebrating brands across drugstore, prestige, and indie — with transparent affiliate links to Sephora, Ulta, Amazon, or direct.",
    Screen: ScreenProducts,
  },
  {
    n: "06",
    label: "AR mirror",
    title: "A drag mom who lives on your face.",
    body:
      "Real-time face-mesh overlays makeup zones on your reflection. Watch the highlighted spots pulse exactly where the step goes.",
    Screen: ScreenMirror,
  },
  {
    n: "07",
    label: "Glow journal",
    title: "Every look, saved privately.",
    body:
      "Favorite your routines, keep a strip of progress snapshots, and come back to looks that already work for you.",
    Screen: ScreenJournal,
  },
];

// ── Section component ────────────────────────────────────────────────
const StepCard = ({ step, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.55, delay: index * 0.06, ease: "easeOut" }}
    viewport={{ once: true, margin: "-40px" }}
    className="flex flex-col items-center text-center shrink-0 w-[260px] snap-center"
    data-testid={`discover-step-${step.n}`}
  >
    <div className="relative mb-5">
      <PhoneFrame testid={`discover-phone-${step.n}`}>
        <step.Screen />
      </PhoneFrame>
      <div className="absolute -top-3 left-2 bg-[#2C2123] text-[#D5A8A4] font-serif italic text-sm px-3 py-1 rounded-full shadow-[0_4px_12px_rgba(44,33,35,0.25)]">
        {step.n}
      </div>
    </div>
    <div className="text-[11px] uppercase tracking-[0.25em] text-[#9B6A6C] mb-1.5">
      {step.label}
    </div>
    <h3 className="font-serif italic text-lg text-[#2C2123] leading-tight mb-2 max-w-[240px]">
      {step.title}
    </h3>
    <p className="text-sm text-[#5C4B51] leading-relaxed max-w-[260px]">
      {step.body}
    </p>
  </motion.div>
);

// ── Autoplay constants ───────────────────────────────────────────────
const AUTOPLAY_INTERVAL_MS = 4000;
// Pause autoplay for this long after the user manually scrolls or
// hovers, so we don't fight their intent.
const AUTOPLAY_PAUSE_AFTER_INTERACT_MS = 8000;

const DiscoverShowcase = () => {
  const { t } = useTranslation();
  const scrollerRef = useRef(null);
  const sectionRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  // Holds autoplay until the carousel is actually visible to the user.
  // First-time visitors land on the hero; if autoplay starts before
  // they scroll down, the carousel could be on step 4 by the time
  // they see it. We snap back to step 1 when they reach the section.
  const [inView, setInView] = useState(false);
  const hasResetForViewRef = useRef(false);
  const pauseTimerRef = useRef(null);

  // Scroll to the Nth phone. We use getBoundingClientRect to be
  // robust against nested flex containers and page-level transforms.
  const scrollTo = (idx) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const children = scroller.firstElementChild?.children;
    if (!children || !children[idx]) return;
    const target = children[idx];
    const sRect = scroller.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    // Distance (in scroller-local coords) from current left to where
    // we want `target` centered.
    const delta =
      tRect.left - sRect.left - (sRect.width - tRect.width) / 2;
    scroller.scrollTo({
      left: Math.max(0, scroller.scrollLeft + delta),
      behavior: "smooth",
    });
  };

  // Keep `activeIdx` in sync with the user's actual scroll position so
  // the progress dots reflect reality, not just autoplay.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;
    const onScroll = () => {
      const children = scroller.firstElementChild?.children;
      if (!children) return;
      const sRect = scroller.getBoundingClientRect();
      const scrollerCenter = sRect.left + sRect.width / 2;
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < children.length; i += 1) {
        const cRect = children[i].getBoundingClientRect();
        const cCenter = cRect.left + cRect.width / 2;
        const d = Math.abs(cCenter - scrollerCenter);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      }
      setActiveIdx(best);
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  // Watch for the section entering the viewport. The first time it
  // does, we jump to step 1 (instant — no smooth animation, since the
  // user hasn't seen anything yet) and only then unblock autoplay.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      // No IO support? Fail open — start playing right away.
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          if (!hasResetForViewRef.current) {
            hasResetForViewRef.current = true;
            // Snap to step 1 without animation so the user always
            // sees the carousel at the start.
            const scroller = scrollerRef.current;
            if (scroller) scroller.scrollLeft = 0;
            setActiveIdx(0);
          }
          setInView(true);
        }
      },
      // Trigger when at least 35% of the section is on screen — that's
      // when the phones are actually readable, not just when the
      // section heading peeks in.
      { threshold: 0.35 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  // Autoplay — advance every N seconds unless the user is actively
  // interacting (hover/focus/drag). Gated on `inView` so the carousel
  // never advances out of sight. Respects prefers-reduced-motion.
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion || userPaused || !inView) return undefined;

    const id = setInterval(() => {
      setActiveIdx((cur) => {
        const next = (cur + 1) % STEPS.length;
        scrollTo(next);
        return next;
      });
    }, AUTOPLAY_INTERVAL_MS);
    return () => clearInterval(id);
  }, [userPaused, inView]);

  const pauseAutoplay = () => {
    setUserPaused(true);
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = setTimeout(() => {
      setUserPaused(false);
    }, AUTOPLAY_PAUSE_AFTER_INTERACT_MS);
  };

  useEffect(() => () => {
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
  }, []);

  return (
    <section
      ref={sectionRef}
      id="discover"
      className="px-6 sm:px-10 py-20 sm:py-28 bg-gradient-to-b from-[#FFFAF7] via-[#FDF2ED] to-[#FFFAF7]"
      data-testid="landing-discover"
    >
      <div className="max-w-6xl mx-auto mb-10">
        <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#9B6A6C] mb-3">
          {t("discover.kicker")}
        </span>
        <h2 className="font-serif italic text-4xl sm:text-5xl leading-tight text-[#2C2123] mb-4 max-w-3xl">
          {t("discover.headline")}
        </h2>
        <p className="text-lg text-[#5C4B51] leading-relaxed max-w-2xl">
          Here's exactly what you'll see inside TruBeauté — from the first
          question to the live AR mirror, the glow journal, and the
          product picks in between.
        </p>
      </div>

      {/* Horizontal scroller; autoplay advances to the next phone
          every ~4s, pauses while hovered or after a manual scroll. */}
      <div
        ref={scrollerRef}
        className="-mx-6 sm:-mx-10 px-6 sm:px-10 overflow-x-auto snap-x snap-mandatory scrollbar-hide"
        onMouseEnter={pauseAutoplay}
        onTouchStart={pauseAutoplay}
        onWheel={pauseAutoplay}
        data-testid="discover-scroller"
      >
        {/* pt-5 gives the floating step-number badge (which uses
            `-top-3` to poke above the phone) room to render inside
            the scroller's clipping box. Without it, `overflow-x-auto`
            implicitly hides overflow-y and cuts the badges off. */}
        <div className="flex gap-8 pt-5 pb-6 min-w-min">
          {STEPS.map((s, i) => (
            <StepCard key={s.n} step={s} index={i} />
          ))}
        </div>
      </div>

      {/* Progress dots — clickable to jump */}
      <div
        className="flex flex-col items-center gap-3 mt-2 text-xs text-[#8A7D82]"
        data-testid="discover-scroll-hint"
      >
        <div
          className="flex gap-1.5"
          role="tablist"
          aria-label={t("discover.tablistAria")}
        >
          {STEPS.map((s, i) => (
            <button
              key={s.n}
              type="button"
              role="tab"
              aria-selected={i === activeIdx}
              aria-label={t("discover.goToStep", { n: s.n, label: s.label })}
              onClick={() => {
                pauseAutoplay();
                scrollTo(i);
                setActiveIdx(i);
              }}
              className={
                i === activeIdx
                  ? "h-1.5 w-6 rounded-full bg-[#9B6A6C] transition-all"
                  : "h-1.5 w-1.5 rounded-full bg-[#D5A8A4]/60 hover:bg-[#D5A8A4] transition-all"
              }
              data-testid={`discover-dot-${s.n}`}
            />
          ))}
        </div>
        <div className="sm:hidden">{t("discover.scrollHint")}</div>
      </div>

      <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
        <Link
          to="/onboarding"
          className="group inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-8 py-4 text-base transition-all duration-300 shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5"
          data-testid="discover-launch-app-btn"
        >
          <ShoppingBag className="h-4 w-4" />
          {t("discover.tryItYourself")}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
        <a
          href="#how"
          className="inline-flex items-center gap-2 text-sm text-[#5D4050] hover:text-[#9B6A6C] underline-offset-4 hover:underline transition-colors"
          data-testid="discover-how-link"
        >
          {t("discover.howLink")}
          <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </section>
  );
};

export default DiscoverShowcase;
